/**
 * RBAC API endpoints
 */
import { api } from './baseApi';
import type {
  PermissionCategory,
  Permission,
  PermissionDetail,
  PermissionsParams,
  Role,
  RoleDetail,
  RolesParams,
  CreateRoleRequest,
  UpdateRoleRequest,
  Employee,
  EmployeeDetail,
  EmployeesParams,
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
  ManagePermissionsRequest,
  ApiResponse,
  PaginatedResponse,
} from '../../types/rbac';

export const rbacApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // ===== Permission Categories =====
    getPermissionCategories: builder.query<ApiResponse<PermissionCategory[]>, void>({
      query: () => '/dashboard/rbac/categories/',
      providesTags: ['PermissionCategories'],
    }),

    // ===== Permissions =====
    getPermissions: builder.query<PaginatedResponse<Permission>, PermissionsParams>({
      query: (params) => ({
        url: '/dashboard/rbac/permissions/',
        params,
      }),
      providesTags: ['Permissions'],
    }),

    getPermission: builder.query<ApiResponse<PermissionDetail>, number>({
      query: (id) => `/dashboard/rbac/permissions/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Permission', id }],
    }),

    // ===== Roles =====
    getRoles: builder.query<PaginatedResponse<Role>, RolesParams | void>({
      query: (params) => ({
        url: '/dashboard/rbac/roles/',
        params: params || {},
      }),
      providesTags: ['Roles'],
    }),

    getRole: builder.query<ApiResponse<RoleDetail>, number>({
      query: (id) => `/dashboard/rbac/roles/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Role', id }],
    }),

    createRole: builder.mutation<ApiResponse<RoleDetail>, CreateRoleRequest>({
      query: (body) => ({
        url: '/dashboard/rbac/roles/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Roles'],
    }),

    updateRole: builder.mutation<ApiResponse<RoleDetail>, { id: number } & UpdateRoleRequest>({
      query: ({ id, ...body }) => ({
        url: `/dashboard/rbac/roles/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Role', id },
        'Roles',
        'Employees',
      ],
    }),

    deleteRole: builder.mutation<ApiResponse<void>, number>({
      query: (id) => ({
        url: `/dashboard/rbac/roles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Roles'],
    }),

    // ===== Employees =====
    getEmployees: builder.query<PaginatedResponse<Employee>, EmployeesParams | void>({
      query: (params) => ({
        url: '/dashboard/rbac/employees/',
        params: params || {},
      }),
      providesTags: ['Employees'],
    }),

    getEmployee: builder.query<ApiResponse<EmployeeDetail>, number>({
      query: (id) => `/dashboard/rbac/employees/${id}/`,
      providesTags: (_result, _error, id) => [{ type: 'Employee', id }],
    }),

    createEmployee: builder.mutation<ApiResponse<EmployeeDetail>, CreateEmployeeRequest>({
      query: (body) => ({
        url: '/dashboard/rbac/employees/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Employees', 'Roles'],
    }),

    updateEmployee: builder.mutation<ApiResponse<EmployeeDetail>, { id: number } & UpdateEmployeeRequest>({
      query: ({ id, ...body }) => ({
        url: `/dashboard/rbac/employees/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Employee', id },
        'Employees',
        'Roles',
      ],
    }),

    deleteEmployee: builder.mutation<ApiResponse<void>, number>({
      query: (id) => ({
        url: `/dashboard/rbac/employees/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Employee', id },
        'Employees',
        'Roles',
      ],
    }),

    activateEmployee: builder.mutation<ApiResponse<{ id: number; status: string; is_active: boolean }>, number>({
      query: (id) => ({
        url: `/dashboard/rbac/employees/${id}/activate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Employee', id },
        'Employees',
      ],
    }),

    suspendEmployee: builder.mutation<ApiResponse<{ id: number; status: string; is_active: boolean }>, number>({
      query: (id) => ({
        url: `/dashboard/rbac/employees/${id}/suspend/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Employee', id },
        'Employees',
      ],
    }),

    deactivateEmployee: builder.mutation<ApiResponse<EmployeeDetail>, number>({
      query: (id) => ({
        url: `/dashboard/rbac/employees/${id}/deactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Employee', id },
        'Employees',
      ],
    }),

    manageEmployeePermissions: builder.mutation<ApiResponse<EmployeeDetail>, { id: number } & ManagePermissionsRequest>({
      query: ({ id, ...body }) => ({
        url: `/dashboard/rbac/employees/${id}/permissions/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Employee', id },
        'Employees',
      ],
    }),

    resendEmployeeCredentials: builder.mutation<ApiResponse<void>, number>({
      query: (id) => ({
        url: `/dashboard/rbac/employees/${id}/resend-credentials/`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetPermissionCategoriesQuery,
  useGetPermissionsQuery,
  useGetPermissionQuery,
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetEmployeesQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useDeleteEmployeeMutation,
  useActivateEmployeeMutation,
  useSuspendEmployeeMutation,
  useDeactivateEmployeeMutation,
  useManageEmployeePermissionsMutation,
  useResendEmployeeCredentialsMutation,
} = rbacApi;

